import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import socketIOClient, {Socket} from 'socket.io-client'
import {getUrlData, LAYOUT_TYPE, WSS_URL} from '../../../config/env'
import {useRootStateSelector} from '../hooks/useRootStateSelector'

export interface SocketContext {
  isConnected: boolean
  socket?: Socket | null
  setSocket?: (value: Socket | undefined) => void
  startSocket: (url?: string) => () => void
}

const Context = createContext<SocketContext | null>(null)

export interface WebSocketProviderProps {
  children?: ReactNode
  autoStart?: boolean
}

export const WebSocketProvider = ({children, autoStart = true}: WebSocketProviderProps) => {
  const [socket, setSocket] = useState<Socket>()
  const [isConnected, setIsConnected] = useState(false)
  const tokens = useRootStateSelector((state) => ({
    default: state?.auth?.token,
    outlet: state?.outlet?.auth?.token,
    customer: state.customerPortal?.auth?.token,
  }))

  useEffect(() => {
    const onConnect = () => {
      setIsConnected(true)
    }
    const onDisconnect = () => {
      setIsConnected(false)
    }
    const onError = () => {
      setIsConnected(false)
    }

    socket?.on('connect', onConnect)
    socket?.on('disconnect', onDisconnect)
    socket?.on('connect_error', onError)

    return () => {
      socket?.off('connect', onConnect)
      socket?.off('disconnect', onDisconnect)
      socket?.off('connect_error', onError)
    }
  }, [socket])

  const jwt = useMemo(() => {
    switch (LAYOUT_TYPE) {
      case 'customer-delegate':
      case 'customer-portal':
        return tokens.customer
      case 'outlet':
        return tokens.outlet
      default:
        return tokens.default
    }
  }, [tokens])

  const startSocket = useCallback(
    (url?: string) => {
      const {organizationCode, outletCode} = getUrlData()
      const wssUrl = url || WSS_URL
      const socket = wssUrl
        ? socketIOClient(wssUrl, {
            transports: ['websocket', 'polling'],
            auth: {
              token: jwt,
              organizationCode,
              outletCode,
            },
          })
        : null
      socket && setSocket(socket)
      return () => {
        socket?.disconnect()
        setSocket(undefined)
      }
    },
    [jwt]
  )

  const value = useMemo((): SocketContext => {
    return {
      socket,
      setSocket,
      isConnected,
      startSocket,
    }
  }, [isConnected, socket, setSocket, startSocket])

  useEffect(() => {
    const {organizationCode, outletCode} = getUrlData()
    if (WSS_URL && autoStart) {
      setSocket(
        socketIOClient(WSS_URL, {
          transports: ['websocket', 'polling'],
          auth: {
            token: jwt,
            organizationCode,
            outletCode,
          },
        })
      )
    }
  }, [autoStart, jwt])

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const useWebSocket = (): SocketContext => {
  const socket = useContext(Context)
  if (!socket) {
    throw new Error('socket provider is not present.')
  }
  return socket
}
